import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface ConsultingPackage {
  id: string
  name: string
  nameEn: string
  nameRu?: string
  description: string
  descriptionEn: string
  descriptionRu?: string
  pricePerHour: number
  hours: number
  totalPrice: number
  features: string[]
  featuresEn: string[]
  featuresRu?: string[]
  icon?: string
  category: string
  categoryEn: string
  categoryRu?: string
}

export interface CartItem extends ConsultingPackage {
  quantity: number
}

interface CartContextType {
  items: CartItem[]
  addToCart: (packageItem: ConsultingPackage, quantity: number) => void
  removeFromCart: (packageId: string) => void
  updateQuantity: (packageId: string, quantity: number) => void
  clearCart: () => void
  getTotalPrice: () => number
  getTotalItems: () => number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

interface CartProviderProps {
  children: ReactNode
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([])

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('consultingCart')
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart))
      } catch (error) {
        console.error('Error loading cart from localStorage:', error)
      }
    }
  }, [])

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('consultingCart', JSON.stringify(items))
  }, [items])

  const addToCart = (packageItem: ConsultingPackage, quantity: number) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === packageItem.id)
      
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === packageItem.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      } else {
        return [...prevItems, { ...packageItem, quantity }]
      }
    })
  }

  const removeFromCart = (packageId: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== packageId))
  }

  const updateQuantity = (packageId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(packageId)
      return
    }
    
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === packageId ? { ...item, quantity } : item
      )
    )
  }

  const clearCart = () => {
    setItems([])
    localStorage.removeItem('consultingCart')
  }

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + item.totalPrice * item.quantity, 0)
  }

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0)
  }

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}















